import React, { useEffect } from "react";
import {
  Snackbar,
  Alert,
  AlertColor,
  SnackbarCloseReason,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { markAsRead, removeNotification } from "../redux/notificationSlice";
import { NotificationPayload } from "../types/notificationTypes";

const NotificationSystem: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notifications } = useAppSelector((state) => state.notifications);
  const [open, setOpen] = React.useState(false);
  const [currentNotification, setCurrentNotification] =
    React.useState<NotificationPayload | null>(null);

  useEffect(() => {
    if (notifications.length > 0 && !currentNotification) {
      // Set the first unread notification as current
      const nextNotification =
        notifications.find((n) => !n.read) || notifications[0];
      setCurrentNotification(nextNotification);
      setOpen(true);
      dispatch(markAsRead(nextNotification.id));
    }
  }, [notifications, currentNotification, dispatch]);

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => {
    if (currentNotification) {
      dispatch(removeNotification(currentNotification.id));
    }
    setCurrentNotification(null);
  };

  if (!currentNotification) return null;

  return (
    <Snackbar
      key={currentNotification.id}
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert
        onClose={handleClose}
        severity={currentNotification.type as AlertColor}
        sx={{ width: "100%" }}
      >
        <strong>{currentNotification.title}</strong>
        <div>{currentNotification.message}</div>
      </Alert>
    </Snackbar>
  );
};

export default NotificationSystem;
